import { useMutation, useQuery } from '@apollo/client';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { Button } from 'components/Button/Button';
import { Container } from 'components/Container/Container';
import { Icon } from 'components/Icon/Icon';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { SortFilter } from 'components/SortFilter/SortFilter';
import { Status } from 'components/Status/Status';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/Table/Table';
import { TableActions } from 'components/TableActions/TableActions';
import { useToast } from 'components/Toast/use-toast';
import { normalizeArray } from 'lib/utils';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AppRoutes } from 'Routes';
import { INFORMATION_DELETE } from 'services/information/mutation';
import { INFORMATION } from 'services/information/query';

const SORT = [
    { value: 'A_Z', label: 'A-Z' },
    { value: 'Z_A', label: 'Z-A' },
    { value: 'SORTING_NUMBER_ASC', label: 'Sorting number (Low to High)' },
    { value: 'SORTING_NUMBER_DESC', label: 'Sorting number (High to Low)' },
];

export const Information = () => {
    const { t } = useTranslation('information');
    const { t: b } = useTranslation('translation');
    const [searchParams, setSearchParams] = useSearchParams();
    const { data, refetch } = useQuery(INFORMATION);
    const navigate = useNavigate();
    const { toast } = useToast();
    const [deleteInformationMutation] = useMutation(INFORMATION_DELETE);

    const informationList = normalizeArray(data?.informations) || [];

    // Set default sort to SORTING_NUMBER_ASC on first load
    useEffect(() => {
        if (!searchParams.get('sort')) {
            const params = new URLSearchParams(searchParams);
            params.set('sort', 'SORTING_NUMBER_ASC');
            setSearchParams(params);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const sort = searchParams.get('sort');
        refetch({
            sort: sort,
        });
    }, [searchParams, refetch]);

    const editInformation = (id: string) => {
        navigate(AppRoutes.editInformation.replace(':id', id));
    };

    const deleteInformation = async (id: string) => {
        try {
            const result = await deleteInformationMutation({
                variables: { information: id },
            });
            
            if (result.data?.informationDelete?.success) {
                toast({
                    title: t('deleteSuccessTitle'),
                    variant: 'success',
                });
                refetch();
            } else {
                toast({
                    title: t('deleteFailTitle'),
                    description: t('deleteFailSubtitle'),
                    variant: 'warning',
                });
            }
        } catch (error) {
            toast({
                title: t('deleteFailTitle'),
                description: t('deleteFailSubtitle'),
                variant: 'warning',
            });
        }
    };

    return (
        <Container>
            <Breadcrumbs crumbs={[b('breadcrumbs.dashboard'), b('breadcrumbs.information')]} />
            <PageTitle className="flex-1">
                <div className="flex justify-between">
                    <div>{t('title')}</div>
                    <div>
                        <Button asChild>
                            <Link to={AppRoutes.newInformation}>
                                <Icon name="company-new" className="h-4 mr-2" />
                                {t('createButton')}
                            </Link>
                        </Button>
                    </div>
                </div>
            </PageTitle>

            <div className="flex justify-between pb-4 gap-4">
                <div className="flex gap-4 flex-wrap">
                    <SortFilter options={SORT} />
                </div>
            </div>

            <div className="mt-4">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[100px]">{t('imageLabel')}</TableHead>
                            <TableHead>{t('titleLabel')}</TableHead>
                            <TableHead>{t('descriptionLabel')}</TableHead>
                            <TableHead>{t('typeLabel')}</TableHead>
                            <TableHead>{t('statusLabel')}</TableHead>
                            <TableHead className="text-right">{b('actions')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {informationList.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                                    {t('noInformation')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            informationList.map((info: any) => (
                                <TableRow key={info.id}>
                                    <TableCell>
                                        {info.image?.url ? (
                                            <img 
                                                src={info.image.url} 
                                                alt={info.title} 
                                                className="w-20 h-20 object-cover rounded"
                                            />
                                        ) : (
                                            <div className="w-20 h-20 bg-zinc-100 rounded flex items-center justify-center">
                                                <Icon name="file" className="h-8 w-8 text-zinc-400" />
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell className="font-medium">{info.title}</TableCell>
                                    <TableCell className="max-w-xs truncate">{info.description}</TableCell>
                                    <TableCell>{info.informationType || '-'}</TableCell>
                                    <TableCell>
                                        <Status status={info.status} />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <TableActions
                                            status={info.status}
                                            onEdit={() => editInformation(info.id)}
                                            onStatusChange={() => deleteInformation(info.id)}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </Container>
    );
};
